/**
 * RR4-style withRouter for class components, backed by react-router v6 hooks.
 */
import React from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from 'react-router-dom';

function createCompatHistory(navigate, location, navigationType) {
  const push = (to, state) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }
    if (typeof to === 'string') {
      navigate(to, state !== undefined ? {state} : undefined);
      return;
    }
    navigate(
      {
        pathname: to.pathname,
        search: to.search ?? '',
        hash: to.hash ?? '',
      },
      {state: state !== undefined ? state : to.state},
    );
  };

  const replace = (to, state) => {
    if (typeof to === 'string') {
      navigate(to, {replace: true, ...(state !== undefined ? {state} : {})});
      return;
    }
    navigate(
      {
        pathname: to.pathname,
        search: to.search ?? '',
        hash: to.hash ?? '',
      },
      {
        replace: true,
        state: state !== undefined ? state : to.state,
      },
    );
  };

  return {
    length: typeof window !== 'undefined' ? window.history.length : 1,
    action: navigationType,
    location,
    push,
    replace,
    createHref(loc) {
      return `${loc.pathname}${loc.search || ''}${loc.hash || ''}`;
    },
    listen() {
      return () => {};
    },
    block() {
      return () => {};
    },
    go(delta) {
      navigate(delta);
    },
    goBack() {
      navigate(-1);
    },
    goForward() {
      navigate(1);
    },
  };
}

export function withRouter(Component) {
  function ComponentWithRouter(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const navigationType = useNavigationType();

    const history = React.useMemo(
      () => createCompatHistory(navigate, location, navigationType),
      [navigate, location, navigationType],
    );

    const match = React.useMemo(
      () => ({
        params,
        isExact: true,
        path: location.pathname,
        url: `${location.pathname}${location.search || ''}`,
      }),
      [params, location.pathname, location.search],
    );

    return (
      <Component {...props} history={history} location={location} match={match} />
    );
  }

  ComponentWithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  return ComponentWithRouter;
}
